import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import AppBar from '@mui/material/AppBar'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Error, Spinner } from '../../common/Error';
import { FormSelect, FormSwitch } from '../../common/form'
import Avatar from '../../common/Avatar'
import TopBar from '../../common/TopBar'
import {
  selectUser,
  logout,
  updateUser,
  doForgotPassword,
} from './authSlice';
import { useListLanguagesQuery, INITIAL_LANGUAGES} from '../../app/services/languages/languagesService'

export const Settings = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  //console.log('user =', user)
  const { loading, error, success } = useSelector((state) => state.auth.me)
  const { data: languages = INITIAL_LANGUAGES } = useListLanguagesQuery()
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Email is invalid'),
    primaryLanguage: Yup.string()
      .required('Primary Language is required'),
    chatLanguage: Yup.string()
      .required('Chat Language is required'),
  })
  const {
    register,
    handleSubmit,
    control,
    formState: { isDirty, errors },
    reset,
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      primaryLanguage: user?.native_lang_id || 'en',
      chatLanguage: user?.default_lang_id || 'en',
      emailNotifications: user?.email_notifications || true,
    },
    resolver: yupResolver(validationSchema)
  })

  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        email: user.email,
        primaryLanguage: user.native_lang_id,
        chatLanguage: user.default_lang_id,
        emailNotifications: user.email_notifications,
      })
    }
  }, [user])

  const language_options = languages.map((l) => {
    return {
      value: l.language,
      name: `${l.name} [${l.language}]`
    }
  })
  const submitForm = (data) => {
    // transform email string to lowercase to avoid case sensitivity issues in login
    data.email = data.email.toLowerCase()
    dispatch(updateUser({
      name: data.name,
      email: data.email,
      native_lang_id: data.primaryLanguage,
      default_lang_id: data.chatLanguage,
      email_notifications: data.emailNotifications,
    }))
  }

  return (
    <Box component='form' noValidate='noValidate' onSubmit={handleSubmit(submitForm)}>
      <TopBar
        title='Profile'
        primaryAction={
          <LoadingButton
            size='medium'
            type='submit'
            variant='contained'
            disabled={!isDirty}
            loading={loading}
          >
            Save
          </LoadingButton>
        }
      />
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          > 
            {user
              ? <Avatar
                  name={user.name}
                  sx={{ height: 80, width: 80, mt: 3, mb: 3 }}
                />
              : <Skeleton
                  variant='circular'
                  animation='wave'
                  height='80px'
                  width='80px'
                  sx={{ mt: 3, mb: 3 }}
                />
            }
          </Box>
          { user
            ? (
              <Box>
                <TextField
                  id='name'
                  autoComplete='full-name'
                  name='name'
                  {...register('name')}
                  required
                  fullWidth
                  label='Name'
                  margin='normal'
                  error={errors.name ? true : false}
                  helperText={errors.name?.message}
                />
                <TextField
                  id='email'
                  autoComplete='email'
                  disabled
                  name='email'
                  {...register('email')}
                  required
                  fullWidth
                  label='Email'
                  margin='normal'
                  error={errors.email ? true : false}
                  helperText={errors.email?.message}
                />
                <FormSelect
                  id='primary-language'
                  name='primaryLanguage'
                  label='Primary Language'
                  control={control}
                  variant='outlined'
                  margin='normal'
                  required
                  fullWidth
                  options={language_options}
                  error={errors.primaryLanguage ? true : false}
                  helperText={'Select the language you use on a daily basis'}
                />
                <FormSelect
                  id='chat-language'
                  name='chatLanguage'
                  label='Chat Language'
                  control={control}
                  variant='outlined'
                  margin='normal'
                  required
                  fullWidth
                  options={language_options}
                  error={errors.chatLanguage ? true : false}
                  helperText={'Select the language you will use to chat'}
                />
                <FormSwitch
                  id='email-notifications'
                  name='emailNotifications'
                  label='Message Notifications'
                  control={control}
                  fullWidth
                  helperText={'Receive message notifications by email'}
                />
              </Box>
            ) : (
              <Stack spacing={3} width='100%'>
                <Skeleton variation='rounded' animation='wave' width='100%' height='70px' />
                <Skeleton variation='rounded' animation='wave' width='100%' height='70px' />
                <Skeleton variation='rounded' animation='wave' width='100%' height='70px' />
                <Skeleton variation='rounded' animation='wave' width='100%' height='70px' />
              </Stack>
            )
          }
        </Box>
      </Container>
    </Box>
  )
}
//                <Box
//                  sx={{
//                    display: 'flex',
//                    justifyContent: 'space-between',
//                    alignItems: 'center',
//                    mx: 1,
//                    my: 2,
//                  }}
//                >
//                  <Typography>
//                    Email notifications
//                  </Typography>
//                  <Stack direction="row" spacing={1} alignItems="center">
//                    <Typography sx={ switchStyle }>Off</Typography>
//                    <Controller
//                      as={Switch}
//                      type='checkbox'
//                      name='emailNotifications'
//                      defaultValue={true}
//                      control={control}
//                    />
//                    <Typography sx={ switchStyle }>On</Typography>
//                  </Stack>
//                </Box>
